import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack } from "@mui/material";
import { Formik, Form } from "formik";
import { CognitoUserPoolType, UserAccountsService, UserGroup } from "gen/clients";
import { SelectManyField } from "components/formikFields/SelectManyField/SelectManyField";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ApiErrorMessage } from "../../ApiErrorMessage/ApiErrorMessage";

// Props for the component
interface Props {
  userPool: CognitoUserPoolType;
  username: string;
  availableGroups: UserGroup[]; // List of all available groups
  userGroups: UserGroup[]; // Current groups assigned to the user
  onSuccess: () => void;
  onClose: () => void;
}

enum FieldName {
  groups = "groups"
}

interface FormValues {
  [FieldName.groups]: string[];
}

const EditUserGroupsDialog: React.FC<Props> = ({
  userPool,
  username,
  availableGroups,
  userGroups,
  onSuccess,
  onClose
}) => {
  const { t } = useTranslation();

  const { mutateAsync, error } = useMutation(UserAccountsService.changeCognitoUserGroups);

  const groupOptions = React.useMemo(
    () =>
      availableGroups.map(g => ({
        label: t(`roles.${g}`),
        value: g
      })),
    [availableGroups, t]
  );

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const selectedGroups = values[FieldName.groups];
      const addGroups = selectedGroups.filter(group => !userGroups.some(userGroup => userGroup === group));
      const removeGroups = userGroups.filter(userGroup => !selectedGroups.some(group => group === userGroup));
      await mutateAsync({
        userPool,
        cognitoUsername: username,
        requestBody: {
          addGroups,
          removeGroups
        }
      });
      onSuccess();
    },
    [mutateAsync, onSuccess, userGroups, userPool, username]
  );

  return (
    <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="xs">
      <DialogTitle>Edit User Roles</DialogTitle>
      <Formik
        initialValues={{
          [FieldName.groups]: userGroups
        }}
        onSubmit={onSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <Stack spacing={2}>
                <SelectManyField name={FieldName.groups} options={groupOptions} />
                {error && <ApiErrorMessage apiError={error} />}
              </Stack>
            </DialogContent>
            <DialogActions>
              <LoadingButton isLoading={isSubmitting} type="submit" color="primary" variant="contained">
                Save
              </LoadingButton>
              <Button onClick={onClose} color="secondary">
                Cancel
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { EditUserGroupsDialog };
